/** @file Main request unit for DALFE. */
import { FoldersResource } from '../resources/folders'
import { WallFolderMappingsResource } from '../resources/wallFolderMappings'
import { DataAccessLayerRequestInit, DataTransport } from './DataTransport'

type DataAccessLayerInit = {
  host: string
  token: string | null
  /** @default false */
  useInsecureHttp?: boolean
  injectedHeaders?: HeadersInit
}

class DataAccessLayer implements DataTransport {
  private rootUrl: URL
  private injectedHeaders: Headers

  constructor(init: DataAccessLayerInit) {
    const { host, token, useInsecureHttp = false, injectedHeaders } = init
    this.rootUrl = new URL(`http${useInsecureHttp ? '' : 's'}://${host}`)
    this.injectedHeaders = new Headers()
    if (token) {
      this.injectedHeaders.set('Authorization', `Bearer ${token}`)
    }
    if (injectedHeaders) {
      new Headers(injectedHeaders).forEach((value, key) => {
        this.injectedHeaders.set(key, value)
      })
    }
  }

  // Data Transport
  async requestProtobuf<TResult>(input: string, init: DataAccessLayerRequestInit<TResult>): Promise<TResult> {
    // Resolve URL
    let url: URL
    if (/^https?:\/\//.test(input)) {
      url = new URL(input)
    } else {
      url = new URL(input, this.rootUrl)
    }

    const { parseResult, headers: initHeaders, ...restOfInit } = init

    // Combine headers
    const headers = new Headers(this.injectedHeaders)
    if (initHeaders) {
      new Headers(initHeaders).forEach((value, key) => {
        headers.set(key, value)
      })
    }
    const resolvedInit: RequestInit = { ...restOfInit, headers }
    const response = await fetch(url.toString(), resolvedInit)
    const base64String = await response.text()
    const message = parseResult(Buffer.from(base64String, 'base64'))
    return message as TResult
  }

  // Resources
  get folders(): FoldersResource {
    return new FoldersResource(this)
  }
  get wallFolderMappings(): WallFolderMappingsResource {
    return new WallFolderMappingsResource(this)
  }
}

export type { DataAccessLayerInit }
export { DataAccessLayer }
